import { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Axios from '@/shared/api/axios';
import StatusModal from '@/components/ui/StatusModal';

/**
 * 訂閱方案選擇 Modal（訂閱/續訂共用）
 */
const SubscriptionPlanModal = ({ show, onHide, onConfirm, mode = 'subscribe', idolId }) => {
    const { t, i18n } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentStatusCode, setCurrentStatusCode] = useState('');
    const statusModalRef = useRef(null);

    // 取得當前操作的偶像 ID
    const currentIdolId = useSelector(state => state.user.idolId);

    // 取得偶像訂閱方案 (9-1)
    const fetchSubscriptionPlans = async () => {
        if (!currentIdolId || !idolId) return;

        setLoading(true);
        try {
            const response = await Axios.post('/idol/get-subscription-plan', {
                idol_id: currentIdolId,
                view_idol_id: idolId
            });

            if (response.data?.status_code === 'SYSTEM_1000' && response.data.data) {
                const data = response.data.data;
                const generatedPlans = generatePlans(data);
                setPlans(generatedPlans);
                if (generatedPlans.length > 0) {
                    setSelectedPlan(generatedPlans[0]);
                }
            } else if (response.data?.status_code) {
                setCurrentStatusCode(response.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Failed to fetch subscription plans:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    // 根據 API 數據生成方案列表
    const generatePlans = (data) => {
        const { monthly_price, discount_1_months = 0, discount_3_months, discount_6_months, discount_12_months } = data;

        // 計算價格：折扣比例表示折掉多少，例如 60 = 折扣 60%，支付 40%
        const calculatePrice = (months, discountPercent) => {
            const originalPrice = monthly_price * months;
            return originalPrice * (1 - discountPercent / 100);
        };

        // 根據語言和數量決定使用單數或複數
        const getDurationText = (duration) => {
            if (i18n.language === 'en' && duration > 1) {
                return t('subscription_duration_plural', { duration });
            }
            return t('subscription_duration', { duration });
        };

        const planConfigs = [
            { months: 1, discount: discount_1_months },
            { months: 3, discount: discount_3_months },
            { months: 6, discount: discount_6_months },
            { months: 12, discount: discount_12_months }
        ];

        return planConfigs.map((config, index) => {
            const price = calculatePrice(config.months, config.discount);
            const originalPrice = monthly_price * config.months;
            const isFree = price === 0;

            let label;
            if (isFree) {
                label = `${getDurationText(config.months)} - ${t('free')}`;
            } else if (config.discount > 0) {
                // 保留最多兩位小數，移除尾部的0
                const formattedPrice = parseFloat(price.toFixed(2));
                label = `${getDurationText(config.months)} - ₣${formattedPrice} FCoin (${config.discount}% ${t('discount')})`;
            } else {
                const formattedPrice = parseFloat(price.toFixed(2));
                label = `${getDurationText(config.months)} - ₣${formattedPrice} FCoin`;
            }

            return {
                id: index + 1,
                duration: config.months,
                price,
                originalPrice,
                discount: config.discount,
                label,
                isFree
            };
        });
    };

    // 當 Modal 打開時載入方案
    useEffect(() => {
        if (show && idolId) {
            fetchSubscriptionPlans();
        }
    }, [show, idolId]);

    const handleConfirm = () => {
        if (selectedPlan) {
            onConfirm(selectedPlan);
        }
    };

    const titleText = mode === 'renew' ? t('renew') : t('subscribe');

    return (
        <>
            <Modal show={show} onHide={onHide} centered>
                <Modal.Header closeButton className="border-bottom pb-3">
                    <Modal.Title className="fw-bold">{titleText}</Modal.Title>
                </Modal.Header>

                <Modal.Body className="py-4">
                    <p className="mb-4">{t('select_subscription_plan')}</p>
                    <div className="d-flex flex-column gap-3">
                        {plans.map((plan) => (
                            <div
                                key={plan.id}
                                className={`p-3 rounded-pill text-center fw-bold cursor-p ${selectedPlan?.id === plan.id
                                    ? 'bg-primary text-white'
                                    : 'border border-2 border-primary text-primary'
                                    }`}
                                onClick={() => setSelectedPlan(plan)}
                            >
                                {plan.label}
                            </div>
                        ))}
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 justify-content-end gap-2">
                    <button className="btn btn-neutral rounded-pill" onClick={onHide}>
                        {t('cancel')}
                    </button>
                    <button
                        className="btn btn-primary rounded-pill"
                        onClick={handleConfirm}
                        disabled={!selectedPlan || loading}
                    >
                        {t('confirm')}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Status Modal */}
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
        </>
    );
};

export default SubscriptionPlanModal;
